import { auditApi } from "../services/auditApi.js";
import { organizationUnitApi } from "../services/organizationUnitApi.js";
import { permissionApi } from "../services/permissionApi.js";
import { roleApi } from "../services/roleApi.js";
import { tenantApi } from "../services/tenantApi.js";
import { userApi } from "../services/userApi.js";
import type { CmtsGridColumn } from "../components/data-grid/index.js";
import type { CmtsFormField } from "../components/forms/index.js";
import type { EntityRecord } from "../types/api.types.js";
import type { CmtsAdminIdentityPageConfig, CmtsAdminIdentityPermissionConfig } from "../types/adminIdentity.types.js";
import { cmtsPermissions } from "./permission.config.js";

const statusColumn: CmtsGridColumn<EntityRecord> = { id: "status", header: "Status", sortable: true, filterable: true };
const updatedColumn: CmtsGridColumn<EntityRecord> = { id: "updatedAt", header: "Updated", sortable: true };

const identityPermissions = {
  tenants: {
    read: cmtsPermissions.administration.tenantsRead,
    create: cmtsPermissions.administration.tenantsCreate,
    update: cmtsPermissions.administration.tenantsUpdate,
    activate: cmtsPermissions.administration.tenantsUpdate,
    deactivate: cmtsPermissions.administration.tenantsDeactivate,
    audit: cmtsPermissions.auditSecurity.auditRead,
  },
  organizationUnits: {
    read: cmtsPermissions.administration.organizationUnitsRead,
    create: cmtsPermissions.administration.organizationUnitsCreate,
    update: cmtsPermissions.administration.organizationUnitsUpdate,
    activate: cmtsPermissions.administration.organizationUnitsUpdate,
    deactivate: cmtsPermissions.administration.organizationUnitsDeactivate,
    audit: cmtsPermissions.auditSecurity.auditRead,
  },
  users: {
    read: cmtsPermissions.administration.usersRead,
    create: cmtsPermissions.administration.usersCreate,
    update: cmtsPermissions.administration.usersUpdate,
    activate: cmtsPermissions.administration.usersUpdate,
    deactivate: cmtsPermissions.administration.usersDeactivate,
    assign: cmtsPermissions.administration.usersAssignRole,
    audit: cmtsPermissions.auditSecurity.auditRead,
  },
  roles: {
    read: cmtsPermissions.administration.rolesRead,
    create: cmtsPermissions.administration.rolesCreate,
    update: cmtsPermissions.administration.rolesUpdate,
    activate: cmtsPermissions.administration.rolesUpdate,
    deactivate: cmtsPermissions.administration.rolesDeactivate,
    assign: cmtsPermissions.administration.rolesAssignPermission,
    audit: cmtsPermissions.auditSecurity.auditRead,
  },
  permissions: {
    read: cmtsPermissions.administration.permissionsRead,
    create: cmtsPermissions.administration.permissionsCreate,
    update: cmtsPermissions.administration.permissionsUpdate,
    activate: cmtsPermissions.administration.permissionsUpdate,
    deactivate: cmtsPermissions.administration.permissionsDeactivate,
    audit: cmtsPermissions.auditSecurity.auditRead,
  },
} satisfies Record<string, CmtsAdminIdentityPermissionConfig>;

const tenantFields: readonly CmtsFormField[] = [
  { name: "tenantCode", label: "Tenant Code", type: "text", required: true, minLength: 2 },
  { name: "tenantName", label: "Tenant Name", type: "text", required: true, minLength: 2 },
  { name: "legalName", label: "Legal Name", type: "text" },
  { name: "country", label: "Country", type: "text" },
  { name: "timezone", label: "Timezone", type: "text" },
  { name: "defaultLocale", label: "Default Locale", type: "text" },
];
const orgFields: readonly CmtsFormField[] = [
  { name: "orgUnitCode", label: "Organization Unit Code", type: "text", required: true, minLength: 2 },
  { name: "orgUnitName", label: "Organization Unit Name", type: "text", required: true, minLength: 2 },
  { name: "parentOrgUnitId", label: "Parent Organization Unit", type: "text", help: "Backend validation controls tenant/org hierarchy consistency." },
  { name: "orgUnitType", label: "Organization Unit Type", type: "text" },
];
const userFields: readonly CmtsFormField[] = [
  { name: "loginId", label: "Login ID", type: "text", required: true, minLength: 2 },
  { name: "displayName", label: "Display Name", type: "text", required: true, minLength: 2 },
  { name: "email", label: "Email", type: "text" },
  { name: "employeeCode", label: "Employee Code", type: "text" },
];
const roleFields: readonly CmtsFormField[] = [
  { name: "code", label: "Role Code", type: "text", required: true, minLength: 2 },
  { name: "name", label: "Role Name", type: "text", required: true, minLength: 2 },
  { name: "description", label: "Description", type: "textarea" },
];
const permissionFields: readonly CmtsFormField[] = [
  { name: "code", label: "Permission Code", type: "text", required: true, minLength: 2 },
  { name: "name", label: "Permission Name", type: "text", required: true, minLength: 2 },
  { name: "module", label: "Module", type: "text" },
  { name: "description", label: "Description", type: "textarea" },
];

export const cmtsTenantAdminIdentityConfig: CmtsAdminIdentityPageConfig = {
  entity: "tenants",
  title: "Tenants",
  subtitle: "Tenant creation, update, activation state and audit trail governed by backend permissions.",
  routeId: "admin.identity.tenants",
  routePath: "/cmts/admin/tenants",
  apiServiceName: "tenantApi",
  api: tenantApi,
  permissions: identityPermissions.tenants,
  columns: [{ id: "tenantCode", header: "Tenant Code", sortable: true, filterable: true }, { id: "tenantName", header: "Tenant Name", sortable: true, filterable: true }, { id: "legalName", header: "Legal Name", sortable: true, filterable: true }, statusColumn, updatedColumn],
  defaultFields: tenantFields,
  rowKey: "id",
  auditModule: "TENANTS",
};

export const cmtsOrganizationUnitAdminIdentityConfig: CmtsAdminIdentityPageConfig = {
  entity: "organization-units",
  title: "Organization Units",
  subtitle: "Organization hierarchy administration with backend validation and dependency blocking.",
  routeId: "admin.identity.organization-units",
  routePath: "/cmts/admin/organization-units",
  apiServiceName: "organizationUnitApi",
  api: organizationUnitApi,
  permissions: identityPermissions.organizationUnits,
  columns: [{ id: "orgUnitCode", header: "Org Unit Code", sortable: true, filterable: true }, { id: "orgUnitName", header: "Org Unit Name", sortable: true, filterable: true }, { id: "parentOrgUnitId", header: "Parent", sortable: true, filterable: true }, { id: "orgUnitType", header: "Type", sortable: true, filterable: true }, statusColumn],
  defaultFields: orgFields,
  rowKey: "id",
  supportsOrgScope: true,
  auditModule: "ORGANIZATION_UNITS",
};

export const cmtsUsersAdminIdentityConfig: CmtsAdminIdentityPageConfig = {
  entity: "users",
  title: "Users",
  subtitle: "User lifecycle, role assignment, organization scope and backend credential-policy governed actions.",
  routeId: "admin.identity.users",
  routePath: "/cmts/admin/users",
  apiServiceName: "userApi",
  api: userApi,
  permissions: identityPermissions.users,
  columns: [{ id: "loginId", header: "Login ID", sortable: true, filterable: true }, { id: "displayName", header: "Display Name", sortable: true, filterable: true }, { id: "email", header: "Email", sortable: true, filterable: true }, statusColumn, updatedColumn],
  defaultFields: userFields,
  rowKey: "id",
  supportsAssignments: true,
  supportsOrgScope: true,
  supportsCredentialPolicy: true,
  auditModule: "USERS",
};

export const cmtsRolesAdminIdentityConfig: CmtsAdminIdentityPageConfig = {
  entity: "roles",
  title: "Roles",
  subtitle: "Role creation, update and role-permission assignment with backend RBAC/SoD blocking.",
  routeId: "admin.identity.roles",
  routePath: "/cmts/admin/roles",
  apiServiceName: "roleApi",
  api: roleApi,
  permissions: identityPermissions.roles,
  columns: [{ id: "code", header: "Role Code", sortable: true, filterable: true }, { id: "name", header: "Role Name", sortable: true, filterable: true }, { id: "description", header: "Description", sortable: true, filterable: true }, statusColumn],
  defaultFields: roleFields,
  rowKey: "id",
  supportsAssignments: true,
  auditModule: "ROLES",
};

export const cmtsPermissionsAdminIdentityConfig: CmtsAdminIdentityPageConfig = {
  entity: "permissions",
  title: "Permissions",
  subtitle: "Permission catalogue creation, update and lifecycle controlled by backend authorization.",
  routeId: "admin.identity.permissions",
  routePath: "/cmts/admin/permissions",
  apiServiceName: "permissionApi",
  api: permissionApi,
  permissions: identityPermissions.permissions,
  columns: [{ id: "code", header: "Permission Code", sortable: true, filterable: true }, { id: "name", header: "Permission Name", sortable: true, filterable: true }, { id: "module", header: "Module", sortable: true, filterable: true }, statusColumn],
  defaultFields: permissionFields,
  rowKey: "id",
  auditModule: "PERMISSIONS",
};

export const cmtsAdminIdentityConfigs = [
  cmtsTenantAdminIdentityConfig,
  cmtsOrganizationUnitAdminIdentityConfig,
  cmtsUsersAdminIdentityConfig,
  cmtsRolesAdminIdentityConfig,
  cmtsPermissionsAdminIdentityConfig,
] as const;

export function findCmtsAdminIdentityConfig(entity: string): CmtsAdminIdentityPageConfig | undefined {
  return cmtsAdminIdentityConfigs.find((config) => config.entity === entity || config.routeId === entity || config.routePath === entity);
}

export { auditApi as cmtsAdminIdentityAuditApi };
